import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '@/components/ui/carousel.tsx'
import { Button } from '@/components/ui/button.tsx'
import LearningStageCircle from '@/components/LearningStageCircle.tsx'
import { MoveLeft, MoveRight } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useTheme } from '@/useTheme.ts'
import useGlobalStore from '@/useGlobalStore.ts'
import { cn } from '@/lib/utils.ts'

const LibraryVocabularyLearningsPracticeResult = ({
	className,
	answerDetails,
	handleOpenWordDetailsModal,
}: {
	className?: string | null
	answerDetails: any
	handleOpenWordDetailsModal: any
}) => {
	const { t } = useTranslation()
	const { themeColor } = useTheme()
	const { availableAppLanguages, appLanguageCode } = useGlobalStore()
	const justLearnedVocab = answerDetails?.justLearnedVocab ?? []
	const improvedLearningsVocab = answerDetails?.improvedLearningsVocab ?? []
	const otherVocab = answerDetails?.otherVocab ?? []

	return (
		<div className={cn('flex flex-col', className)}>
			{justLearnedVocab.length > 0 ? (
				<>
					<h1 className="font-semibold mx-auto my-3">{t('Just learned')}:</h1>
					<Carousel className="mx-20">
						<CarouselContent>
							{justLearnedVocab.map((libVoc: any, index: number) => (
								<CarouselItem
									onClick={() => handleOpenWordDetailsModal(libVoc.library_vocabulary)}
									key={index}>
									<div
										className="w-2/3 h-48 mx-auto select-none text-black dark:text-white rounded-lg font-bold text-lg flex items-center justify-center shadow-[-13px_-13px_22px_-9px_rgba(0,_0,_0,_0.15)]"
										style={{
											backgroundColor: themeColor === 'dark' ? '#3d3d3d' : '#e1e1e1',
										}}>
										{libVoc.library_vocabulary.word}
									</div>
								</CarouselItem>
							))}
						</CarouselContent>
						<CarouselPrevious
							className={availableAppLanguages[appLanguageCode].dir === 'rtl' ? 'rotate-180' : ''}
						/>
						<CarouselNext
							className={availableAppLanguages[appLanguageCode].dir === 'rtl' ? 'rotate-180' : ''}
						/>
					</Carousel>
				</>
			) : improvedLearningsVocab.length > 0 ? (
				<div className="flex">
					<div
						className="tracking-wide mx-2 font-bold my-auto"
						style={{ writingMode: 'vertical-lr', transform: 'scale(-1)' }}>
						{t('Improved learnings')}
					</div>
					<div className="flex-grow me-2">
						{improvedLearningsVocab.map((item: any, index: number) => (
							<Button
								onClick={() => handleOpenWordDetailsModal(item.library_vocabulary)}
								variant="secondary"
								className="flex w-full items-center gap-x-2 px-3 py-2 my-1 min-h-9 h-auto"
								key={index}>
								<div className="min-w-0 flex-1 text-base text-left whitespace-normal break-words">
									{item.library_vocabulary.word}
								</div>
								<div className="h-9 w-9 flex justify-center items-center">
									<LearningStageCircle learningStage={item.previous_learning_stage} />
								</div>
								<div className="h-9 w-9 flex justify-center items-center">
									{availableAppLanguages[appLanguageCode].dir === 'rtl' ? (
										<MoveLeft className="w-6" />
									) : (
										<MoveRight className="w-6" />
									)}
								</div>
								<div className="h-9 w-9 flex justify-center items-center">
									<LearningStageCircle learningStage={item.new_learning_stage} />
								</div>
							</Button>
						))}
					</div>
				</div>
			) : (
				<div className="flex flex-col items-center justify-center">
					<h2>{t('Learned nothing new.')}</h2>
					<h5>{t('Try again later')}</h5>
				</div>
			)}
			{otherVocab.length > 0 && (
				<div className="flex mt-4">
					<div
						className="tracking-wide mx-2 font-bold my-auto"
						style={{ writingMode: 'vertical-lr', transform: 'scale(-1)' }}>
						{t('Try again later')}
					</div>
					<div className="flex-grow me-10">
						<div className="w-3/5 mx-auto">
							{otherVocab.map((item: any, index: number) => (
								<Button
									onClick={() => handleOpenWordDetailsModal(item.library_vocabulary)}
									variant="secondary"
									className="flex w-full items-center gap-x-2 px-3 py-2 my-1 min-h-9 h-auto"
									key={index}>
									<div className="min-w-0 flex-1 text-base text-left whitespace-normal break-words">
										{item.library_vocabulary.word}
									</div>
									<div className="h-9 w-9 flex justify-center items-center">
										<LearningStageCircle learningStage={item.new_learning_stage} />
									</div>
								</Button>
							))}
						</div>
					</div>
				</div>
			)}
		</div>
	)
}

export default LibraryVocabularyLearningsPracticeResult
